/**
 * FirmsSection.jsx
 * ---------------------------------------------------------------------------
 * Section 4. Law firm supply-side positioning + engagement form.
 *
 * Interest types:
 *   - case_intake     → /api/firm-inquiry
 *   - ai_training     → /api/firm-inquiry
 *   - data_at_rest    → /api/firm-inquiry
 *   - coconsolex      → /api/coconsolex-inquiry  (priority/standard triage)
 *
 * When the user arrives via a CoConsoleX subtab CTA, Home.jsx writes
 * `csx:firmInquiryPrefill` to sessionStorage; this component reads it on
 * mount, pre-selects the interest, and surfaces a small "Referred from"
 * ribbon at the top of the form.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Section from "../ui/Section.jsx";
import { H2, Lede } from "../ui/Heading.jsx";
import Button from "../ui/Button.jsx";
import Field from "../ui/Field.jsx";
import { api } from "../../lib/apiClient.js";
import { tokens } from "../../lib/tokens.js";
import { SUBTABS } from "../../lib/coconsolexTokens.js";

const EMPTY = {
  firmName: "",
  contactName: "",
  email: "",
  practiceArea: "",
  interest: "",
  firmTier: "",
  jurisdiction: "",
};

const INTEREST_OPTIONS = [
  { value: "case_intake", label: "Qualified case intake" },
  { value: "ai_training", label: "AI enablement & training" },
  { value: "data_at_rest", label: "Data At Rest™ pilot" },
  { value: "coconsolex", label: "CoConsoleX co-counsel engagement" },
];

const TIER_OPTIONS = [
  { value: "amlaw_100", label: "AmLaw 100" },
  { value: "amlaw_200", label: "AmLaw 200" },
  { value: "large_boutique", label: "Large boutique" },
  { value: "mid_market", label: "Mid-market firm" },
  { value: "small_firm", label: "Small firm / boutique" },
  { value: "other", label: "Other" },
];

const FEATURES = [
  ["Structured case summaries", "Every matter arrives as a normalized profile."],
  ["Reduced intake burden", "Triage, scoring, and routing performed upstream."],
  ["Higher signal-to-noise ratio", "Only cases aligned with your practice reach you."],
  ["Faster response cycles", "Clear facts enable faster qualification decisions."],
];

function findSubtabTitle(slug) {
  const match = SUBTABS.find((s) => s.slug === slug);
  return match ? match.title : "";
}

export default function FirmsSection() {
  const [form, setForm] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [referrerSubtab, setReferrerSubtab] = useState("");

  // Read prefill written by Home.jsx when the user arrived via a
  // /coconsolex/* subtab CTA. One-shot: consume and clear.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("csx:firmInquiryPrefill");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.interest) {
        setForm((f) => ({ ...f, interest: parsed.interest }));
      }
      if (parsed && parsed.from) setReferrerSubtab(parsed.from);
      sessionStorage.removeItem("csx:firmInquiryPrefill");
    } catch {
      sessionStorage.removeItem("csx:firmInquiryPrefill");
    }
  }, []);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const isCoConsoleX = form.interest === "coconsolex";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    let res;
    if (isCoConsoleX) {
      res = await api.submitCoConsoleXInquiry({
        firmName: form.firmName,
        contactName: form.contactName,
        email: form.email,
        practiceArea: form.practiceArea,
        firmTier: form.firmTier,
        jurisdiction: form.jurisdiction,
        referrerSubtab,
      });
    } else {
      res = await api.submitFirmInquiry({
        firmName: form.firmName,
        contactName: form.contactName,
        email: form.email,
        practiceArea: form.practiceArea,
        interest: form.interest,
      });
    }

    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Submission failed.");
      return;
    }
    setSubmitted(true);
  };

  const reset = () => {
    setSubmitted(false);
    setForm(EMPTY);
    setReferrerSubtab("");
  };

  const referrerTitle = referrerSubtab ? findSubtabTitle(referrerSubtab) : "";

  return (
    <Section id="firms" label="III · Law Firms" tone="paper">
      <div
        data-firms-grid
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          gap: 96,
          alignItems: "start",
        }}
      >
        {/* Copy column */}
        <div>
          <H2>Qualified cases. Structured intake.</H2>
          <Lede>
            Replace fragmented intake with organized case intelligence. Reduce
            noise. Increase conversion.
          </Lede>

          <div style={{ marginTop: 72 }}>
            {FEATURES.map(([title, body], i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "60px 1fr",
                  gap: 24,
                  padding: "28px 0",
                  borderTop: `1px solid ${tokens.divider}`,
                  borderBottom:
                    i === FEATURES.length - 1
                      ? `1px solid ${tokens.divider}`
                      : "none",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 18,
                    color: tokens.inkSoft,
                    letterSpacing: "0.1em",
                  }}
                >
                  0{i + 1}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 22,
                      color: tokens.ink,
                      marginBottom: 4,
                      fontWeight: 500,
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 14,
                      color: tokens.inkSoft,
                      lineHeight: 1.6,
                    }}
                  >
                    {body}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CoConsoleX cross-link card */}
          <Link
            to="/coconsolex"
            style={{
              display: "block",
              marginTop: 40,
              padding: "28px 32px",
              border: `1px solid ${tokens.divider}`,
              background: "#FAF7F0",
              textDecoration: "none",
              color: tokens.ink,
            }}
          >
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 13,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#A8862B",
                marginBottom: 6,
              }}
            >
              Adjacent offering
            </div>
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 24,
                fontWeight: 500,
                lineHeight: 1.2,
                marginBottom: 6,
              }}
            >
              CoConsole<span style={{ color: "#A8862B" }}>X</span> · Air-gapped
              co-counsel
            </div>
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                color: tokens.inkSoft,
                lineHeight: 1.6,
              }}
            >
              For privilege-critical matters that cannot leave a sovereign
              perimeter. Read the fifteen-section doctrine →
            </div>
          </Link>
        </div>

        {/* Form card */}
        <div
          data-firms-form
          style={{
            background: tokens.bg,
            border: `1px solid ${tokens.divider}`,
            padding: "48px 52px",
            position: "sticky",
            top: 120,
          }}
        >
          {referrerTitle && !submitted && (
            <div
              style={{
                marginBottom: 24,
                padding: "12px 16px",
                background: "#F0EDE5",
                borderLeft: "3px solid #A8862B",
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: tokens.inkSoft,
                letterSpacing: "0.04em",
              }}
            >
              Referred from CoConsoleX ·{" "}
              <span style={{ color: tokens.ink, fontWeight: 500 }}>
                {referrerTitle}
              </span>
            </div>
          )}

          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 22,
              color: tokens.ink,
              marginBottom: 8,
            }}
          >
            Firm Engagement
          </div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color: tokens.inkSoft,
              paddingBottom: 24,
              borderBottom: `1px solid ${tokens.divider}`,
              marginBottom: 36,
            }}
          >
            Request access, AI enablement, Data At Rest™ pilots, or CoConsoleX
            co-counsel engagements.
          </div>

          {!submitted ? (
            <form onSubmit={submit}>
              <Field
                label="Firm Name"
                name="firmName"
                value={form.firmName}
                onChange={update}
              />
              <Field
                label="Contact Name"
                name="contactName"
                value={form.contactName}
                onChange={update}
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={update}
              />
              <Field
                label="Practice Area"
                name="practiceArea"
                value={form.practiceArea}
                onChange={update}
                placeholder="e.g., Plaintiff Personal Injury"
              />
              <Field
                label="Area of Interest"
                name="interest"
                value={form.interest}
                onChange={update}
                options={INTEREST_OPTIONS}
              />

              {isCoConsoleX && (
                <>
                  <Field
                    label="Firm Tier"
                    name="firmTier"
                    value={form.firmTier}
                    onChange={update}
                    options={TIER_OPTIONS}
                  />
                  <Field
                    label="Jurisdiction(s)"
                    name="jurisdiction"
                    value={form.jurisdiction}
                    onChange={update}
                    placeholder="e.g., Federal, NY, EU"
                  />
                </>
              )}

              {error && (
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13,
                    color: "#9B2C2C",
                    marginBottom: 16,
                    paddingLeft: 12,
                    borderLeft: "2px solid #9B2C2C",
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Submitting…" : "Request Engagement"}
                </Button>
              </div>
            </form>
          ) : (
            <div>
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 26,
                  color: tokens.ink,
                  marginBottom: 16,
                  lineHeight: 1.2,
                }}
              >
                Received. Thank you.
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14,
                  color: tokens.inkSoft,
                  lineHeight: 1.6,
                  marginBottom: 24,
                }}
              >
                A CaseSignalHQ representative will respond within one business
                day to schedule an initial briefing.
              </div>
              <Button variant="ghost" onClick={reset}>
                Submit Another
              </Button>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
